import { ACP_REQUEST_STATUS } from "@modules/app/service/app.constant.ts";
import PollingUtils from "@utils/polling.utils.ts";
import { ref } from "vue";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import WaitModal from "@plugins/modal/components/WaitModal.vue";
import {
  ContentSubpageParams,
  useContentSubpage,
} from "@plugins/content-subpage/composables/content-subpage.ts";

export const MAX_INSTRUCTION_LENGTH = 30000;
export const NUM_OF_ELEMENTS = 20;
export const MAX_NUM_OF_ELEMENTS = 50;

interface UseAcpParams {
  contentSubpageParams?: ContentSubpageParams;
  waitModalProcessingTitle?: string;
  waitModalProcessingContent?: string;
}

interface AcpResponse {
  code: string;
  data?: {
    status?: string;
  };
}

type PollingFunc = (params: { requestId: string }) => Promise<unknown>;

export function useAcp(acpParams: UseAcpParams) {
  const contentSubpage = useContentSubpage(acpParams.contentSubpageParams);
  const modal = useModal();

  // polling related
  const readingTimer = ref<NodeJS.Timeout | null>(null);
  const readingTime = ref<number>(0);
  const status = ref<string>(ACP_REQUEST_STATUS.PENDING);
  const MAX_RESPONSE_TIME = 5;
  const continuePolling = ref<boolean>(true);
  const apiResponse = ref<AcpResponse | null>(null);

  const pollForStatus = async (requestId: string, pollingFunc: PollingFunc): Promise<string> => {
    initializeTimer("processing");

    try {
      const response: AcpResponse = await PollingUtils.pollingWithInterval(
        { requestId: requestId },
        pollingFunc,
        continueCheckingForStatus,
        Date.now(),
      );
      handlePollResponse(response);
      return response;
    } catch (error) {
      handleTimeout();
      throw error;
    }
  };

  const continueCheckingForStatus = (response: AcpResponse): boolean => {
    if (!continuePolling.value) {
      return false;
    }

    if (!response || response.code !== "ok") {
      return false;
    }

    const pollStatus = response.data?.status;
    return (
      pollStatus === ACP_REQUEST_STATUS.PENDING || pollStatus === ACP_REQUEST_STATUS.PROCESSING
    );
  };

  const handlePollResponse = (response: AcpResponse) => {
    if (continueCheckingForStatus(response)) {
      return;
    }
    status.value = "complete";
    apiResponse.value = response;
    handleWaitModal();
  };

  const handleTimeout = () => {
    status.value = "timeout";
    handleWaitModal();
  };

  const initializeTimer = (pollStatus: string) => {
    resetTimer();
    setStatus(pollStatus);
    startTimer();
    continuePolling.value = true;
  };

  const resetTimer = () => {
    if (readingTimer.value) {
      clearInterval(readingTimer.value);
      readingTimer.value = null;
    }
    readingTime.value = 0;
    status.value = null;
    apiResponse.value = null;
  };

  const startTimer = () => {
    readingTimer.value = setInterval(() => {
      readingTime.value += 1;
      if (readingTime.value > MAX_RESPONSE_TIME) {
        handleWaitModal();
      }
    }, 1000);
  };

  const setStatus = (pollStatus: string) => {
    status.value = pollStatus;
  };

  const handleWaitModal = () => {
    if (
      status.value === "timeout" ||
      apiResponse.value?.code === "error" ||
      (status.value === "complete" && apiResponse.value?.code === "ok")
    ) {
      closeWaitModal();
    } else {
      showWaitModal();
    }
  };

  const closeWaitModal = () => {
    if (readingTimer.value) {
      clearInterval(readingTimer.value);
      readingTimer.value = null;
    }
    modal.removeAll();
    contentSubpage.setVisible(true);
  };

  const showWaitModal = () => {
    contentSubpage.setVisible(false);
    modal.show(WaitModal, {
      state: status.value,
      stateMessage: {
        processing: {
          title: acpParams?.waitModalProcessingTitle ?? "",
          content: acpParams?.waitModalProcessingContent ?? "",
        },
      },
    });
  };

  const cleanUpPollInstance = () => {
    continuePolling.value = false;
    if (readingTimer.value) {
      clearInterval(readingTimer.value);
      readingTimer.value = null;
    }
  };

  return {
    // polling related
    pollForStatus,
    cleanUpPollInstance,

    ...contentSubpage,
  };
}
